import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import type { BuildingSavingsDetailDataDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import { BasicInfo } from "web_myplann/Portfolio/Components/PortfolioSections/BasicInfo";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { SectionDivider } from "web_myplann/Portfolio/Components/SectionDidiver";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import {
	getPortfolioFrequencyValue,
	getPortfolioValueDate,
} from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { DataCardPreset } from "web_myplann/Shared/Components/DataCard";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	DataRowCard,
	type DataRowCardItem,
} from "web_myplann/Shared/Components/DataRowCard";
import { useNumberFormatter } from "web_myplann/Shared/Hooks/useNumberFormatter";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import { EMPTY_DETAIL_FIELD } from "web_myplann/Utils/constants";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";
import { DataLineChart } from "../DataLineChart/DataLineChart";

export const BuildingSavings: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();
	const formatNumber = useNumberFormatter();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailSectionLoader />;
	}

	const { daysData, totalValue, totalInvestedValue, toDate, showChartInfo } =
		calculateGraphData(
			reporting?.reportingData,
			reporting?.summary ?? undefined,
		);

	const portfolioData = baseData.data as BuildingSavingsDetailDataDTO;

	const savings = portfolioData.payment
		? getPortfolioFrequencyValue(
				portfolioData.payment ?? null,
				currencySign,
				portfolioData.paymentFrequency ?? null,
				t,
			)
		: EMPTY_DETAIL_FIELD;

	const formattedAppreciation = formatNumber(portfolioData.appreciation);
	const appreciation = formattedAppreciation && `${formattedAppreciation} %`;

	const stateContribution =
		typeof portfolioData.stateContribution !== "boolean"
			? ""
			: t(Resources.Common[portfolioData.stateContribution ? "yes" : "no"]);

	const basicInfoItems: DataRowCardItem[] = [
		{
			label: t(Resources.PortfolioDetail.SharedLabels.name),
			value: baseData.clientName,
		},
		{
			label: t(Resources.PortfolioDetail.SharedLabels.contractNumber),
			value: baseData.contractNumber,
		},
		{
			label: t(Resources.PortfolioDetail.SharedLabels.producent),
			value: baseData.producer,
		},
		{
			label: t(Resources.PortfolioDetail.SharedLabels.product),
			value: baseData.product,
		},
		{
			label: t(Resources.PortfolioDetail.BuildingSavings.savings),
			value: savings,
		},
		{
			label: t(Resources.PortfolioDetail.BuildingSavings.appreciation),
			value: appreciation,
		},
		{
			label: t(Resources.PortfolioDetail.BuildingSavings.stateContribution),
			value: stateContribution,
		},
	];

	return (
		<Stack>
			<DataLineChart
				hideDivider
				totalValue={totalValue}
				totalEvaluationValue={reporting?.summary?.totalEvaluationValue ?? 0}
				totalEvaluationPercent={reporting?.summary?.totalEvaluationPercent ?? 0}
				daysData={daysData}
				currencySign={currencySign}
				toDate={toDate}
				showChartInfo={showChartInfo}
			/>

			{showChartInfo && (
				<>
					<DataRow
						label={t(Resources.PortfolioDetail.SharedLabels.targetAmount)}
						value={moneyFormatter(
							portfolioData.targetAmount,
							currencySign,
							"",
							2,
						)}
					/>

					<DataRow
						label={t(Resources.Portfolio.Chart.InvestedTogether)}
						value={moneyFormatter(totalInvestedValue, currencySign, "", 2)}
					/>

					<DataRow
						label={t(Resources.Common.ToDate)}
						value={getPortfolioValueDate(daysData, baseData)}
					/>
				</>
			)}

			{daysData.length !== 0 && <SectionDivider />}

			<DataRowCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={<BasicInfo data={baseData} />}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny
				}
				items={basicInfoItems}
			/>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</Stack>
	);
};
